import { create } from "zustand";

/**
 * Toast severities. `success` and `error` are the original pair, kept as the
 * `success: boolean` argument so every existing call site is untouched.
 *
 * `warning` is the third state, added for outcomes that are neither: the save
 * LANDED, and there is still something the user has to know — "the PDF saved,
 * but N class copies were not updated". A red toast there reads as a failed
 * save and invites a pointless retry; a green one hides the part that needs a
 * human. react-toastify already renders all three.
 */
export type NotificationSeverity = "success" | "error" | "warning";

interface NotificationState {
  notify: boolean;
  isSuccess: boolean;
  severity: NotificationSeverity;
  msg: string;
  /**
   * `severity` is optional and defaults to the old success/error mapping, so
   * `showNotif(msg)` and `showNotif(msg, true)` behave exactly as before.
   */
  showNotif: (
    message: string,
    success?: boolean,
    severity?: NotificationSeverity,
  ) => void;
  hideNotif: () => void;
}

const useNotificationStore = create<NotificationState>((set) => ({
  notify: false,
  isSuccess: false,
  severity: "error",
  msg: "",
  showNotif: (
    message: string,
    success: boolean = false,
    severity?: NotificationSeverity,
  ) =>
    set({
      notify: true,
      isSuccess: success,
      severity: severity ?? (success ? "success" : "error"),
      msg: message,
    }),
  hideNotif: () => set({ notify: false, msg: "" }),
}));

export default useNotificationStore;
